import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon, Save, CheckCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import AuthImage from '../common/AuthImage';

const OpcionModal = ({ isOpen, onClose, onSave, initialData, preguntaId }) => {
  const [formData, setFormData] = useState({
    texto: '',
    esCorrecta: false
  });
  const [imagenFile, setImagenFile] = useState(null);
  const [previewImagen, setPreviewImagen] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        texto: initialData.texto || '',
        esCorrecta: initialData.esCorrecta || false
      });
      // Mostrar imagen existente si hay
      if (initialData.imagen) {
        setPreviewImagen(initialData.imagen);
      } else {
        setPreviewImagen(null);
      }
      setImagenFile(null); // No hay archivo nuevo al inicio
    } else {
      setFormData({
        texto: '',
        esCorrecta: false
      });
      setImagenFile(null);
      setPreviewImagen(null);
    }
    setErrors({});
  }, [initialData, isOpen]);

  const handleImagenChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, imagen: 'La imagen no debe superar 5MB' });
        return;
      }
      
      // Validar tipo
      if (!file.type.startsWith('image/')) {
        setErrors({ ...errors, imagen: 'Solo se permiten imágenes' });
        return;
      }

      setImagenFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImagen(reader.result);
      };
      reader.readAsDataURL(file);
      setErrors({ ...errors, imagen: null });
    }
  };

  const handleRemoveImage = () => {
    setImagenFile(null);
    setPreviewImagen(null);
    // Si estamos editando y había una imagen, marcarla para eliminar
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.texto.trim()) {
      newErrors.texto = 'El texto de la opción es obligatorio';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    console.log('🔍 OpcionModal - Submit:', {
      hasImagenFile: !!imagenFile,
      hasPreviewImagen: !!previewImagen,
      opcionId: initialData?.id,
      preguntaId
    });

    // Si hay una imagen nueva, usar FormData
    if (imagenFile) {
      console.log('📤 Enviando FormData (con imagen nueva)');
      const formDataToSend = new FormData();
      
      const opcionJson = {
        texto: formData.texto,
        esCorrecta: formData.esCorrecta,
        preguntaId: parseInt(preguntaId)
      };
      
      const jsonBlob = new Blob([JSON.stringify(opcionJson)], { type: 'application/json' });
      formDataToSend.append('opcion', jsonBlob);
      formDataToSend.append('imagen', imagenFile);

      onSave(formDataToSend, initialData?.id, preguntaId, true); // true = tiene imagen
    } else {
      // Si NO hay imagen nueva, enviar solo JSON
      console.log('📤 Enviando JSON (sin imagen nueva)');
      const opcionData = {
        texto: formData.texto,
        esCorrecta: formData.esCorrecta,
        preguntaId: parseInt(preguntaId)
      };

      onSave(opcionData, initialData?.id, preguntaId, false); // false = sin imagen nueva
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? '✏️ Editar Opción' : 'Nueva Opción de Respuesta'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Texto de la opción */}
        <div>
          <label className="block text-sm font-semibold text-neutral-700 mb-2">
            Texto de la Opción <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.texto}
            onChange={(e) => setFormData({ ...formData, texto: e.target.value })}
            placeholder="Escribe aquí el texto de la opción de respuesta..."
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none ${
              errors.texto ? 'border-red-500' : 'border-neutral-300'
            }`}
            rows={3}
          />
          {errors.texto && (
            <p className="text-red-500 text-sm mt-1">{errors.texto}</p>
          )}
        </div>

        {/* Imagen */}
        <div>
          <label className="block text-sm font-semibold text-neutral-700 mb-2">
            <ImageIcon className="w-4 h-4 inline mr-2" />
            Imagen (Opcional)
          </label>
          
          {!previewImagen ? (
            <label className="cursor-pointer">
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-neutral-300 rounded-xl hover:border-primary hover:bg-primary/5 transition-all group">
                <div className="w-12 h-12 rounded-full bg-neutral-100 group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                  <Upload className="w-6 h-6 text-neutral-400 group-hover:text-primary transition-colors" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-neutral-700 group-hover:text-primary transition-colors">
                    Click para seleccionar
                  </p>
                  <p className="text-xs text-neutral-500">
                    PNG, JPG, GIF hasta 5MB
                  </p>
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImagenChange}
                className="hidden"
              />
            </label>
          ) : (
            <div className="relative">
              {/* Si es una imagen nueva (File), mostrar con img normal */}
              {imagenFile ? (
                <img 
                  src={previewImagen} 
                  alt="Preview" 
                  className="w-full h-48 object-contain rounded-xl border-2 border-neutral-200 bg-neutral-50"
                />
              ) : (
                /* Si es una imagen existente (URL), usar AuthImage */
                <AuthImage
                  src={previewImagen}
                  alt="Imagen actual"
                  className="w-full h-48 object-contain rounded-xl border-2 border-neutral-200 bg-neutral-50"
                />
              )}
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-colors"
                title="Eliminar imagen"
              >
                <X className="w-4 h-4" />
              </button>
              {imagenFile && (
                <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                  {imagenFile?.name}
                </div>
              )}
            </div>
          )}
          
          {errors.imagen && (
            <p className="text-red-500 text-sm mt-1">{errors.imagen}</p>
          )}
        </div>

        {/* Respuesta correcta */}
        <div className={`p-4 rounded-xl border-2 transition-all ${
          formData.esCorrecta 
            ? 'bg-green-50 border-green-300' 
            : 'bg-neutral-50 border-neutral-200 hover:border-neutral-300'
        }`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.esCorrecta}
              onChange={(e) => setFormData({ ...formData, esCorrecta: e.target.checked })}
              className="w-5 h-5 text-green-600 border-neutral-300 rounded focus:ring-green-500 mt-0.5"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className={`w-5 h-5 ${formData.esCorrecta ? 'text-green-600' : 'text-neutral-400'}`} />
                <span className={`font-semibold ${formData.esCorrecta ? 'text-green-800' : 'text-neutral-700'}`}>
                  Esta es la respuesta correcta
                </span>
              </div>
              <p className="text-sm text-neutral-600">
                Marca esta casilla si esta opción es la respuesta correcta a la pregunta
              </p>
            </div>
          </label>
        </div>

        {/* Información adicional */}
        {formData.esCorrecta && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm text-green-800">
              <strong>✓ Respuesta correcta marcada:</strong> Los estudiantes verán esta opción como correcta en sus resultados.
            </p>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            className="px-6"
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            className="px-6 gap-2"
          >
            <Save className="w-4 h-4" />
            {initialData ? 'Actualizar' : 'Crear'} Opción
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default OpcionModal;
